/**
 * Unsaved-changes guards.
 *
 * `useBeforeUnloadWhenDirty` — the browser's native prompt on tab close / reload.
 *
 * `useUnsavedChangesGuard` — also intercepts IN-APP navigation while `when` is true. The app uses
 * `BrowserRouter` (no `useBlocker`), so:
 *   (a) a capture-phase document click listener intercepts same-origin internal `<a href>`
 *       navigations (sidebar, bottom tabs, Settings nav, back links) and opens the dialog;
 *   (b) browser Back: while dirty a sentinel history entry is pushed; on popstate it is re-pushed
 *       and the dialog opens with "back" as the pending action.
 * Discard continues the pending navigation; Save awaits `onSave()` and continues only when it
 * resolves true (otherwise the dialog closes and the page shows its own save error); Keep editing /
 * Escape closes the dialog. Programmatic `navigate()` calls are not intercepted.
 */

import { createElement, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';

const SENTINEL_KEY = '__unsavedChangesGuard';
const ROUTER_BASENAME = '/landlord';

export function useBeforeUnloadWhenDirty(when: boolean): void {
  useEffect(() => {
    if (!when) return;
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);
}

type PendingNavigation = { kind: 'path'; path: string } | { kind: 'back' };

/** App-relative destination of a plain left-click on an internal link, or null to let it through. */
function internalNavigationTarget(e: MouseEvent, current: Location = window.location): string | null {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const anchor = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>('a[href]') : null;
  if (!anchor || anchor.hasAttribute('download')) return null;
  if (anchor.target && anchor.target !== '_self') return null;
  const url = new URL(anchor.href, current.href);
  if (url.origin !== current.origin) return null;
  if (url.pathname !== ROUTER_BASENAME && !url.pathname.startsWith(`${ROUTER_BASENAME}/`)) return null;
  if (url.pathname === current.pathname && url.search === current.search) return null;
  return `${url.pathname.slice(ROUTER_BASENAME.length) || '/'}${url.search}${url.hash}`;
}

export function useUnsavedChangesGuard(opts: {
  when: boolean;
  onSave: () => Promise<boolean>;
  /** Dialog body; defaults to generic wording. */
  message?: string;
}): { dialog: ReactNode } {
  const { when, onSave, message } = opts;
  const navigate = useNavigate();
  const [pending, setPending] = useState<PendingNavigation | null>(null);
  const [saving, setSaving] = useState(false);
  // Set once the landlord chose to leave: the guard stands down so the navigation goes through.
  const bypass = useRef(false);
  const ignoreNextPop = useRef(false);

  useBeforeUnloadWhenDirty(when);

  // (a) internal link clicks
  useEffect(() => {
    if (!when) return;
    // A host that stays mounted across steps (the listing guide) becomes dirty again after the
    // landlord chose to leave once: guard the new edits too.
    bypass.current = false;
    function onClick(e: MouseEvent) {
      if (bypass.current) return;
      const path = internalNavigationTarget(e);
      if (path === null) return;
      e.preventDefault();
      e.stopPropagation();
      setPending({ kind: 'path', path });
    }
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [when]);

  // (b) browser back
  useEffect(() => {
    if (!when) return;
    window.history.pushState({ [SENTINEL_KEY]: true }, '');
    function onPopState() {
      if (ignoreNextPop.current) {
        ignoreNextPop.current = false;
        return;
      }
      if (bypass.current) return;
      if ((window.history.state as Record<string, unknown> | null)?.[SENTINEL_KEY]) return;
      window.history.pushState({ [SENTINEL_KEY]: true }, '');
      setPending({ kind: 'back' });
    }
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      // Dirty state ended without leaving (saved / discarded in place): drop the sentinel entry.
      if (!bypass.current && (window.history.state as Record<string, unknown> | null)?.[SENTINEL_KEY]) {
        ignoreNextPop.current = true;
        window.history.back();
      }
    };
  }, [when]);

  const proceed = useCallback(
    (nav: PendingNavigation) => {
      bypass.current = true;
      setPending(null);
      // Replace the sentinel entry rather than stacking on it, so Back from the destination
      // returns to this page instead of to a duplicate entry.
      if (nav.kind === 'path') navigate(nav.path, { replace: true });
      else window.history.go(-2);
    },
    [navigate],
  );

  const keepEditing = useCallback(() => setPending(null), []);
  const discard = useCallback(() => {
    if (pending) proceed(pending);
  }, [pending, proceed]);
  const save = useCallback(async () => {
    if (!pending || saving) return;
    setSaving(true);
    let ok = false;
    try {
      ok = await onSave();
    } finally {
      setSaving(false);
    }
    if (ok) proceed(pending);
    else setPending(null);
  }, [pending, saving, onSave, proceed]);

  const dialog = createElement(UnsavedChangesDialog, {
    isOpen: pending !== null,
    message,
    saving,
    onKeepEditing: keepEditing,
    onDiscard: discard,
    onSave: () => void save(),
  });

  return { dialog };
}
